import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import MobileNav from "@/components/MobileNav";
import { ArrowLeft, ExternalLink, Music, Calendar, Gift, CreditCard } from "lucide-react";

const PersonalizePage = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const venuesRes = await axios.get(`${API}/venues`, { withCredentials: true });
      if (venuesRes.data?.length > 0) {
        const integrationsRes = await axios.get(
          `${API}/venues/${venuesRes.data[0].venue_id}/integrations`,
          { withCredentials: true }
        );
        setIntegrations(integrationsRes.data);
      }
    } catch (error) {
      console.error("Error fetching integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "music": return Music;
      case "app": return Calendar;
      case "checkout": return CreditCard;
      default: return Gift;
    }
  };

  const categorizedIntegrations = {
    music: integrations.filter((i) => i.type === "music"),
    app: integrations.filter((i) => i.type === "app"),
    checkout: integrations.filter((i) => i.type === "checkout"),
  };

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="personalize-page">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 z-40 bg-white/90 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <Link to="/wallet" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold">Personalize</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-in">
        {/* Hero */}
        <div className="text-center py-6">
          <h2 className="font-display text-2xl font-bold text-black mb-2">
            Ways to make your experience, yours
          </h2>
          <p className="text-gray-600">
            Connect with venue apps and services
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Music */}
            {categorizedIntegrations.music.length > 0 && (
              <section>
                <h3 className="font-display font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">
                  Music and Ambiance
                </h3>
                <div className="space-y-3">
                  {categorizedIntegrations.music.map((integ) => {
                    const Icon = getIcon(integ.type);
                    return (
                      <a
                        key={integ.integration_id}
                        href={integ.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Card className="border border-gray-200 hover:border-gray-400 card-hover">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                              <Icon className="w-6 h-6 text-black" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-black">{integ.name}</h4>
                              <p className="text-sm text-gray-600">{integ.description}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </CardContent>
                        </Card>
                      </a>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Apps */}
            {categorizedIntegrations.app.length > 0 && (
              <section>
                <h3 className="font-display font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">
                  Apps and Services
                </h3>
                <div className="space-y-3">
                  {categorizedIntegrations.app.map((integ) => {
                    const Icon = getIcon(integ.type);
                    return (
                      <a
                        key={integ.integration_id}
                        href={integ.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Card className="border border-gray-200 hover:border-gray-400 card-hover">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                              <Icon className="w-6 h-6 text-black" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-black">{integ.name}</h4>
                              <p className="text-sm text-gray-600">{integ.description}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </CardContent>
                        </Card>
                      </a>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Checkout */}
            {categorizedIntegrations.checkout.length > 0 && (
              <section>
                <h3 className="font-display font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">
                  Payment
                </h3>
                <div className="space-y-3">
                  {categorizedIntegrations.checkout.map((integ) => {
                    const Icon = getIcon(integ.type);
                    return (
                      <a
                        key={integ.integration_id}
                        href={integ.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Card className="border border-gray-200 hover:border-gray-400 card-hover">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center">
                              <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-black">{integ.name}</h4>
                              <p className="text-sm text-gray-600">{integ.description}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </CardContent>
                        </Card>
                      </a>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Coming Soon */}
            <section>
              <h3 className="font-display font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">
                Coming Soon
              </h3>
              <div className="space-y-3">
                <Card className="border border-gray-200 bg-gray-50">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#E60023] flex items-center justify-center text-white font-bold text-lg">
                      P
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-black">Pinterest Integration</h4>
                      <p className="text-sm text-gray-600">Import your boards directly</p>
                    </div>
                    <Badge variant="outline" className="text-gray-500">Soon</Badge>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200 bg-gray-50">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#1DB954] flex items-center justify-center text-white font-bold text-lg">
                      S
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-black">Spotify Connect</h4>
                      <p className="text-sm text-gray-600">Play your playlists at the venue</p>
                    </div>
                    <Badge variant="outline" className="text-gray-500">Soon</Badge>
                  </CardContent>
                </Card>
              </div>
            </section>
          </>
        )}
      </main>

      <MobileNav />
    </div>
  );
};

export default PersonalizePage;
